import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  TrendingUp, CheckCircle2, ArrowRight, ArrowLeft,
  MapPin, Star, Search, FileText, Image, MessageSquare
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import MarketingHeader from '@/components/MarketingHeader';
import MarketingFooter from '@/components/MarketingFooter';
import SEO from '@/components/SEO';

const chapters = [
  {
    title: 'Google Business Profile Optimization',
    icon: MapPin,
    intro: 'Your Google Business Profile is the foundation of local SEO. It\'s often the first thing potential customers see.',
    tasks: [
      { task: 'Claim and verify your listing', detail: 'Go to business.google.com and complete the verification process (usually a postcard or phone call).' },
      { task: 'Complete every field', detail: 'Fill out hours, services, attributes, and description. Google favors complete profiles.' },
      { task: 'Choose the right categories', detail: 'Select your primary category carefully - it\'s the most important ranking factor. Add relevant secondary categories.' },
      { task: 'Add photos weekly', detail: 'Businesses with photos get 42% more direction requests. Add photos of your location, team, products, and work.' },
      { task: 'Post updates regularly', detail: 'Google Posts appear in your listing. Share updates, offers, and events at least weekly.' },
      { task: 'Enable messaging', detail: 'Let customers message you directly through your listing. Respond within 24 hours.' },
    ]
  },
  {
    title: 'Review Management',
    icon: Star,
    intro: 'Reviews are crucial for local rankings and customer trust. Quantity, quality, and recency all matter.',
    tasks: [
      { task: 'Ask every satisfied customer', detail: 'Create a simple process: send a follow-up email or text with a direct link to leave a review.' },
      { task: 'Respond to all reviews', detail: 'Thank positive reviewers by name. For negative reviews, apologize, address concerns, and offer to make it right offline.' },
      { task: 'Never buy or fake reviews', detail: 'Google can detect fake reviews and will penalize your listing severely.' },
      { task: 'Diversify review platforms', detail: 'While Google matters most, also build reviews on Yelp, Facebook, and industry-specific sites.' },
      { task: 'Address negative reviews quickly', detail: 'A thoughtful response to a negative review can actually improve your reputation.' },
      { task: 'Use review insights', detail: 'Look for patterns in feedback to improve your business operations.' },
    ]
  },
  {
    title: 'On-Page Local SEO',
    icon: FileText,
    intro: 'Your website needs to clearly signal to Google where you are and what areas you serve.',
    tasks: [
      { task: 'Include city/region in title tags', detail: 'Example: "Plumber in Las Vegas | Smith Plumbing" - put location near the front of titles.' },
      { task: 'Create location-specific pages', detail: 'If you serve multiple areas, create unique pages for each with relevant local content.' },
      { task: 'Add NAP to every page', detail: 'Name, Address, Phone number should appear in your footer consistently across all pages.' },
      { task: 'Embed a Google Map', detail: 'Add an embedded map on your contact page showing your location.' },
      { task: 'Use local schema markup', detail: 'Add LocalBusiness schema to help Google understand your business details.' },
      { task: 'Write local content', detail: 'Blog about local events, news, or topics relevant to your community.' },
    ]
  },
  {
    title: 'Citation Building',
    icon: Search,
    intro: 'Citations are mentions of your business name, address, and phone number on other websites.',
    tasks: [
      { task: 'Ensure NAP consistency', detail: 'Your business name, address, and phone must be identical everywhere. Even small variations hurt rankings.' },
      { task: 'Claim major directories', detail: 'Start with Yelp, Yellow Pages, Bing Places, Apple Maps, and Facebook.' },
      { task: 'Find industry directories', detail: 'Every industry has specific directories. A restaurant needs TripAdvisor; a contractor needs Angi.' },
      { task: 'Clean up duplicates', detail: 'Search for your business and merge or delete any duplicate listings.' },
      { task: 'Update when you move', detail: 'Old addresses cause confusion. Update all citations if you change locations.' },
      { task: 'Monitor for accuracy', detail: 'Use tools like Moz Local or BrightLocal to track citation health.' },
    ]
  },
  {
    title: 'Local Link Building',
    icon: TrendingUp,
    intro: 'Links from other local websites signal to Google that you\'re a trusted local business.',
    tasks: [
      { task: 'Join local business associations', detail: 'Chamber of Commerce, BNI, and trade associations often link to member businesses.' },
      { task: 'Sponsor local events', detail: 'Event websites typically link to sponsors. Choose events your customers attend.' },
      { task: 'Partner with complementary businesses', detail: 'A wedding photographer might partner with venues, florists, and planners for mutual links.' },
      { task: 'Get featured in local news', detail: 'Pitch story ideas to local journalists. Newsworthiness beats self-promotion.' },
      { task: 'Support local causes', detail: 'Genuine community involvement often results in mentions and links.' },
      { task: 'Create linkable local resources', detail: 'A "Best Coffee Shops in [City]" guide can earn links from other local sites.' },
    ]
  },
  {
    title: 'Mobile & Technical SEO',
    icon: Search,
    intro: 'Most local searches happen on mobile devices. Your site must work flawlessly on phones.',
    tasks: [
      { task: 'Test mobile usability', detail: 'Use Google\'s Mobile-Friendly Test. Fix any issues it identifies.' },
      { task: 'Ensure fast load times', detail: 'Aim for under 3 seconds. Compress images, enable caching, consider a CDN.' },
      { task: 'Make phone numbers clickable', detail: 'Use tel: links so mobile users can call with one tap.' },
      { task: 'Simplify mobile navigation', detail: 'Large tap targets, clear menus, and prominent contact information.' },
      { task: 'Enable location permissions', detail: 'If relevant, ask users to share location to show nearest services.' },
      { task: 'Test on real devices', detail: 'Browser dev tools aren\'t enough. Test on actual phones and tablets.' },
    ]
  },
];

const GuideSEOPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="guide-seo-page">
      <SEO 
        title="Local SEO Playbook - Rank Higher in Local Search"
        description="Step-by-step guide to ranking higher in local search results and getting more customers through your door. Google Business Profile, reviews, citations, and more."
        keywords="local SEO, Google Business Profile, local search ranking, local SEO guide, Google Maps SEO, local business marketing, citation building, review management"
        url="/resources/guides/local-seo"
      />
      
      <MarketingHeader />

      {/* Hero Section */}
      <section className="pt-16 pb-12 sm:pt-20 sm:pb-16 bg-gradient-to-b from-orange-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <Link to="/resources/guides" className="inline-flex items-center gap-2 text-orange-600 hover:text-orange-700 mb-6 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            Back to Guides
          </Link>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 rounded-full mb-6">
            <TrendingUp className="h-4 w-4 text-orange-600" />
            <span className="text-sm font-medium text-orange-700">SEO & Marketing</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-carbon-black mb-4" data-testid="guide-title">
            Local SEO Playbook
          </h1>
          <p className="text-xl text-slate-gray leading-relaxed">
            Step-by-step guide to ranking higher in local search results and getting more customers through your door.
          </p>
        </div>
      </section>

      {/* Stats Banner */}
      <section className="py-8 bg-orange-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-3 gap-4 text-center text-white">
            <div>
              <div className="text-2xl sm:text-3xl font-bold">46%</div>
              <div className="text-sm opacity-90">of Google searches have local intent</div>
            </div>
            <div>
              <div className="text-2xl sm:text-3xl font-bold">76%</div>
              <div className="text-sm opacity-90">visit a business within a day of searching</div>
            </div>
            <div>
              <div className="text-2xl sm:text-3xl font-bold">28%</div>
              <div className="text-sm opacity-90">of local searches result in a purchase</div>
            </div>
          </div>
        </div>
      </section>

      {/* Chapters */}
      {chapters.map((chapter, chapterIdx) => {
        const IconComponent = chapter.icon;
        return (
          <section key={chapterIdx} className={`py-12 sm:py-16 ${chapterIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
            <div className="max-w-4xl mx-auto px-4 sm:px-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 flex items-center justify-center flex-shrink-0">
                  <IconComponent className="h-6 w-6 text-white" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black">{chapter.title}</h2>
              </div>
              
              <p className="text-slate-gray mb-8 ml-16">{chapter.intro}</p>
              
              <div className="space-y-4 ml-16">
                {chapter.tasks.map((item, itemIdx) => (
                  <div key={itemIdx} className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="font-semibold text-carbon-black mb-1">{item.task}</h3>
                        <p className="text-sm text-slate-gray">{item.detail}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );
      })}

      {/* Quick Wins */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-8 text-center">Quick Wins to Start Today</h2>
          
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="text-2xl font-bold text-orange-500 mb-2">15 min</div>
              <h3 className="font-bold text-carbon-black mb-2">Claim Google Business Profile</h3>
              <p className="text-sm text-slate-gray">If you haven't already, this is the single most important thing you can do.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="text-2xl font-bold text-orange-500 mb-2">10 min</div>
              <h3 className="font-bold text-carbon-black mb-2">Ask 3 Customers for Reviews</h3>
              <p className="text-sm text-slate-gray">Send a personal text or email with a direct link to your Google review page.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="text-2xl font-bold text-orange-500 mb-2">20 min</div>
              <h3 className="font-bold text-carbon-black mb-2">Add Photos to Your Listing</h3>
              <p className="text-sm text-slate-gray">Upload 5-10 quality photos of your business, team, and products/services.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="text-2xl font-bold text-orange-500 mb-2">30 min</div>
              <h3 className="font-bold text-carbon-black mb-2">Check NAP Consistency</h3>
              <p className="text-sm text-slate-gray">Search your business name and verify your info is identical everywhere.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-4">
            Want Us to Handle Your Local SEO?
          </h2>
          <p className="text-lg text-slate-gray mb-8 max-w-2xl mx-auto">
            We help local businesses dominate search results in their area. Let's discuss a strategy for your business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/resources/consultation">
              <Button className="gradient-fearless text-white px-8 py-6 text-lg font-semibold rounded-xl hover:opacity-90 transition-all w-full sm:w-auto">
                Book a Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/resources/guides">
              <Button variant="outline" className="px-8 py-6 text-lg font-semibold rounded-xl border-2 border-orange-400 text-orange-600 hover:bg-orange-50 transition-all w-full sm:w-auto">
                More Guides
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default GuideSEOPage;
